/*
 * The .be-pixel-text guard.
 *
 * Every component here takes its label as a ReactNode and wraps it in a
 * `.be-pixel-text` run so the coverage faces get their per-engine treatment.
 * On Gecko that treatment is `isolation: isolate; mix-blend-mode: multiply`,
 * which is identity ONLY for ink on a transparent run -- if anything inside the
 * run paints a background, that background gets multiplied against whatever is
 * behind it and goes visibly dark.
 *
 * That constraint was previously enforced one element out, on the component's
 * own label element (see Box.tsx's legend, which masks the fieldset border).
 * It is not enough: `label` is a ReactNode, so a caller can pass a whole
 * CONTROL. BBox's demo passes a <BeMenuField>, whose well paints --be-panel,
 * and it multiplied against the box surface -- the exact documented failure,
 * reached through the caller instead of through our own markup.
 *
 * So the treatment is applied only to TEXT, which is all it was ever for. The
 * wrapper element still renders either way -- components position their labels
 * with it and dropping it would trade a paint bug for a layout one -- but a
 * non-text child only gets the component's own class, never .be-pixel-text.
 * Pixel text nested inside such a child is handled by its own component.
 */

import type { ReactNode } from "react";

/*
 * TWO CALL PATTERNS. Pick by whether the labelled element paints a background:
 *
 *   plain run          {pixelText(label, "be-thing__label")}
 *   paints a background <span className="be-thing__label">{pixelText(label)}</span>
 *
 * The second is required whenever the class carries ANY background, including
 * one that only appears in a state. `.be-checkbox__label` and `.be-radio__label`
 * paint a nav-blue focus underline as a gradient: with the class merged onto the
 * treated run, multiply darkened it from rgb(0,0,229) to rgb(0,0,128) whenever
 * the control was focused. That was removed once (02166a5) and reinstated a
 * commit later (2888f76), so it is worth grepping the CSS for `background` on a
 * label class before merging rather than copying a neighbouring call site.
 */

/** True for nodes that are pure text (incl. arrays/fragments of text). */
export function isTextual(node: ReactNode): boolean {
	if (typeof node === "string" || typeof node === "number") return true;
	if (Array.isArray(node)) return node.length > 0 && node.every(isTextual);
	return false;
}

/**
 * Wrap `node` in the component's label run, adding `.be-pixel-text` only when
 * the node is text. `className` is the component's own label class, if any.
 */
export function pixelText(node: ReactNode, className?: string): ReactNode {
	const cls = [className, isTextual(node) ? "be-pixel-text" : null].filter(Boolean).join(" ");
	return <span className={cls || undefined}>{node}</span>;
}
